import { useEffect } from 'react';
import { useStore } from '@store/appStore';
import { isOutdated, reloadForNewVersion } from '@utils/versionCheck';

// While the page stays visible, how often to check for a newer build.
const ACTIVE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

// Keeps open tabs/apps on the live build (see utils/versionCheck):
//  - at startup, and when the page becomes visible again (tab switched back to, laptop woken, mobile
//    app resumed): reload straight away - nothing is half done yet / the user has just come back. If a
//    reload already happened in the last few minutes, show the "new version" bar instead (no loops).
//    Startup matters too: browsers restoring tabs after a restart (or going "back") deliberately start
//    the page from their cached copy, even an outdated one - seen in Bugsnag as a pre-Friday build
//    starting fresh at ucsyd a day after the release, failing on the role-checked endpoints.
//  - every 30 minutes while visible: never reload under the user - show the bar with a Reload button.
export function useVersionCheck() {
  const setNewVersionAvailable = useStore((state) => state.setNewVersionAvailable);

  useEffect(() => {
    let checking = false;
    const check = async (onOutdated: () => void) => {
      if (checking || document.visibilityState !== 'visible') return;
      checking = true;
      try {
        if (await isOutdated()) onOutdated();
      } finally {
        checking = false;
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      check(() => {
        if (!reloadForNewVersion()) setNewVersionAvailable(true);
      });
    };

    const interval = setInterval(() => check(() => setNewVersionAvailable(true)), ACTIVE_CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    // Startup (a tab restored hidden is checked once it's shown).
    onVisible();
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [setNewVersionAvailable]);
}

export default useVersionCheck;
